import type {ReactNode} from 'react';
import {Video} from '@remotion/media';
import {AbsoluteFill, Easing, Img, Sequence, interpolate, staticFile, useCurrentFrame} from 'remotion';
import {CONTENT} from './content';
import {T} from './theme';

type Travel = 'up' | 'down' | 'left' | 'right' | 'push';
type Scene = {name:string; start:number; end:number; enter?:Travel; exit?:Travel};
const SCENES: Scene[] = [
  {name:'01 · الوجه والإشعار',start:0,end:75,exit:'up'},
  {name:'02 · الجوال والمصروف',start:75,end:144,enter:'up',exit:'left'},
  {name:'03 · الإنسان والحل',start:144,end:204,enter:'left',exit:'push'},
  {name:'04 · دخول إلى ProMax',start:204,end:315,enter:'push',exit:'up'},
  {name:'05 · رقم واضح',start:315,end:408,enter:'up',exit:'left'},
  {name:'06 · أضف المصروف',start:408,end:525,enter:'left',exit:'down'},
  {name:'07 · تم الحفظ',start:525,end:630,enter:'down',exit:'push'},
  {name:'08 · الهوية والشراء',start:630,end:750,enter:'push'},
];
const ease=Easing.bezier(.24,.05,.22,1);
const clamp=(n:number)=>Math.max(0,Math.min(1,n));
const reveal=(f:number,at=0,frames=14)=>ease(clamp((f-at)/frames));
const VECTOR: Record<Exclude<Travel,'push'>,{x:number;y:number}> = {
  up:{x:0,y:-1}, down:{x:0,y:1}, left:{x:-1,y:0}, right:{x:1,y:0},
};
const transitionFrames=18;
const travelSpeed=12; // deliberate 360px/second, matched on BOTH sides of every edit.

// Constant velocity immediately before and after the cut, smooth acceleration into it,
// smooth deceleration after it. Matches direction and speed, not just a crossfade.
const outDistance=(f:number,length:number)=>{
  const t=Math.max(0,f-(length-transitionFrames));
  return t<8?travelSpeed*t*t/16:travelSpeed*(4+t-8);
};
const inDistance=(f:number)=>{
  if(f>=14)return 0;
  if(f<=8)return travelSpeed*(14-f);
  const t=(f-8)/6;
  return travelSpeed*6*(t*t*t-t*t-t+1);
};
const cameraPose=(f:number,length:number,enter?:Travel,exit?:Travel)=>{
  let x=0,y=0,zoom=1,blur=0;
  const end=f-(length-transitionFrames);
  if(enter && f<14){
    const d=inDistance(f);
    if(enter==='push')zoom-=d*.00085;
    else{x-=VECTOR[enter].x*d;y-=VECTOR[enter].y*d;}
    blur=Math.max(blur,23*Math.pow(1-f/14,1.1));
  }
  if(exit && end>0){
    const d=outDistance(f,length);
    if(exit==='push')zoom+=d*.00085;
    else{x+=VECTOR[exit].x*d;y+=VECTOR[exit].y*d;}
    blur=Math.max(blur,23*clamp((end-3)/14));
  }
  // Overscan the entire moving frame so even the fastest whip never reveals its matte.
  const requiredOverscan=Math.max(Math.abs(x)*2/1080,Math.abs(y)*2/1920);
  zoom+=requiredOverscan*1.16;
  return {x,y,zoom,blur};
};

const CameraShot=({scene,children}:{scene:Scene;children:ReactNode})=>{
  const f=useCurrentFrame();
  const length=scene.end-scene.start;
  const {x,y,zoom,blur}=cameraPose(f,length,scene.enter,scene.exit);
  // Underlay prevents a black flash when the moving camera uncovers a border.
  return <AbsoluteFill style={{overflow:'hidden',background:'#09130e'}}>
    <AbsoluteFill style={{background:'radial-gradient(circle at 50% 46%,#31543b 0%,#09120d 65%,#060b08 100%)'}}/>
    <AbsoluteFill style={{
      transform:'translate('+x+'px,'+y+'px) scale('+zoom+')',
      transformOrigin:'540px 960px',
      filter:'blur('+blur+'px)',
      willChange:'transform, filter',
    }}>
      {children}
    </AbsoluteFill>
  </AbsoluteFill>;
};

const Film=({src,zoom=1.13,top=0}:{src:string;zoom?:number;top?:number})=>{
  const f=useCurrentFrame();
  const scale=zoom+.019*clamp(f/80);
  return <AbsoluteFill style={{overflow:'hidden',background:'#0a120f'}}>
    <Video src={staticFile(src)} muted style={{
      position:'absolute',width:'100%',height:'100%',objectFit:'cover',
      transform:'scale('+scale+') translateY('+top+'px)',
      transformOrigin:'center center',
    }}/>
    <AbsoluteFill style={{background:'linear-gradient(180deg,rgba(1,8,6,.15),transparent 24%,rgba(1,9,6,.16) 44%,rgba(2,10,6,.84) 83%,rgba(2,9,6,.92))'}}/>
  </AbsoluteFill>;
};

const Gradient=()=> <AbsoluteFill style={{background:'radial-gradient(ellipse at 50% 48%,#153124 0%,#0b1810 45%,#050b08 100%)'}}>
  <AbsoluteFill style={{opacity:.18,backgroundImage:'linear-gradient(90deg,transparent 49.9%,#e5c35822 50%,transparent 50.1%)'}}/>
</AbsoluteFill>;

const Logo=({center=false}:{center?:boolean})=><div style={{
  position:'absolute',left:center?0:80,right:center?0:undefined,top:center?560:210,
  display:'flex',alignItems:'center',justifyContent:center?'center':'flex-start',gap:18,
  fontFamily:'Arial',fontWeight:800,fontSize:center?94:37,color:'#f3eee0',
}}>
  <Img src={staticFile('brand/promax.png')} style={{width:center?132:46,height:center?132:46,borderRadius:center?26:9}}/>
  <span>ProMax<span style={{color:T.gold}}>.</span></span>
</div>;

const CenterText=({
  children,y=1120,size=106,alpha=1,gold=false,
}:{
  children:ReactNode;y?:number;size?:number;alpha?:number;gold?:boolean;
})=><div dir="rtl" style={{
  position:'absolute',left:95,right:95,top:y,color:gold?T.gold:'#fff9e8',
  fontSize:size,fontWeight:800,lineHeight:1.18,textAlign:'center',opacity:alpha,
  letterSpacing:-1,textShadow:'0 12px 45px rgba(0,0,0,.84)',
}}>{children}</div>;

const FocusBar=({y=1330,alpha=1}:{y?:number;alpha?:number})=><div style={{
  position:'absolute',width:180,height:7,left:450,top:y,borderRadius:10,
  opacity:alpha,background:T.gold,boxShadow:'0 0 28px #e5c35855',
}}/>;

const FaceShot=()=>{
  const f=useCurrentFrame();
  const focus=1-reveal(f,14,10);
  return <AbsoluteFill>
    <Film src="stock/person-worried.mp4" zoom={1.12}/>
    <CenterText y={1090} size={111} alpha={focus}>نزل <span style={{color:T.gold}}>راتبك؟</span></CenterText>
    <FocusBar y={1260} alpha={focus}/>
  </AbsoluteFill>;
};

// One actual visual object crosses the edit at frame 75 without resetting position,
// acceleration or layer identity. The camera pans downward as it follows the
// "falling" salary receipt into the phone scene.
const SalaryReceipt=({frame}:{frame:number})=>{
  if(frame<12 || frame>=102)return null;
  const alpha=reveal(frame,12,11)*(1-reveal(frame,90,12));
  const t=Math.max(0,frame-55);
  const y=755+5*t+.18*t*t;
  const tilt=frame<=55?0:Math.min(13,(frame-55)*.35);
  return <div dir="rtl" style={{
    position:'absolute',left:145,right:145,top:y,height:132,
    padding:'18px 30px',background:'rgba(8,30,19,.96)',
    border:'2px solid rgba(229,195,88,.8)',borderRadius:24,
    display:'flex',alignItems:'center',justifyContent:'space-between',
    boxSizing:'border-box',pointerEvents:'none',opacity:alpha,
    transform:'rotate('+tilt+'deg) scale('+(1-.12*reveal(frame,75,22))+')',
    boxShadow:'0 20px 55px #000b,0 0 45px #e5c35824',
  }}>
    <span style={{fontSize:40,fontWeight:700}}>إيداع راتب</span>
    <span style={{fontFamily:'Arial',fontSize:69,fontWeight:800,color:T.gold}}>+8,000</span>
  </div>;
};

const PhoneShot=()=>{
  const f=useCurrentFrame();
  const steps=[{label:'إيجار',number:'−2,200'},{label:'قسط',number:'−1,000'},{label:'فواتير',number:'−450'}];
  const index=Math.min(2,Math.floor(Math.max(0,f-17)/14));
  // The salary receipt gets exclusive attention as it falls across the cut.
  // Only then does the phone's first expense become the visual target.
  const appear=reveal(f,30,11)*(1-reveal(f,52,11));
  const question=reveal(f,18,11)*(1-reveal(f,39,11));
  return <AbsoluteFill>
    <Film src="stock/phone-close.mp4" zoom={1.09}/>
    <CenterText y={790} size={107} alpha={question}>وين <span style={{color:T.gold}}>راحت؟</span></CenterText>
    <div style={{position:'absolute',left:124,right:124,top:990,opacity:appear,
      background:'rgba(9,26,18,.93)',border:'1px solid #e5c35888',
      borderRadius:28,padding:'28px 32px',display:'flex',justifyContent:'space-between',
      direction:'rtl',alignItems:'center',boxShadow:'0 23px 60px #000a',
    }}>
      <span style={{fontSize:47,fontWeight:700}}>{steps[index].label}</span>
      <span style={{fontSize:70,fontWeight:800,fontFamily:'Arial',color:T.gold}}>{steps[index].number}</span>
    </div>
    <FocusBar y={1175} alpha={appear}/>
  </AbsoluteFill>;
};

const SofaShot=()=>{
  const f=useCurrentFrame();const alpha=reveal(f,12,15)*(1-reveal(f,43,10));
  return <AbsoluteFill>
    <Film src="stock/person-phone.mp4" zoom={1.12}/>
    <CenterText y={1080} size={89} alpha={alpha}>المشكلة مو في الراتب.</CenterText>
    <CenterText y={1208} size={94} gold alpha={alpha}>في وضوح الخطة.</CenterText>
    <FocusBar y={1345} alpha={alpha}/>
  </AbsoluteFill>;
};

const DashboardShot=()=>{
  const f=useCurrentFrame(),p=.76+.24*reveal(f,0,14);
  return <AbsoluteFill>
    <Gradient/><Logo/>
    <div style={{
      position:'absolute',left:87,right:87,top:570,padding:12,overflow:'hidden',borderRadius:38,
      border:'1px solid #e5c35867',background:'#0b1812',
      boxShadow:'0 0 105px #e5c35822,0 45px 100px #000b',
      opacity:p,transform:'scale('+(.9+.1*p)+')',
    }}>
      <Img src={staticFile('screens/dashboard.png')} style={{width:'100%',borderRadius:25,display:'block'}}/>
    </div>
    <CenterText y={1280} size={78} alpha={reveal(f,34,16)*(1-reveal(f,79,12))}>
      كل الأرقام... <span style={{color:T.gold}}>بمكان واحد.</span>
    </CenterText>
    <FocusBar y={1430} alpha={reveal(f,30,15)*(1-reveal(f,79,12))}/>
  </AbsoluteFill>;
};

const SurplusShot=()=>{
  const f=useCurrentFrame(),p=.74+.26*reveal(f,0,12);
  return <AbsoluteFill>
    <Gradient/><Logo/>
    <div style={{position:'absolute',top:590,left:190,width:700,height:700,borderRadius:500,
      border:'7px solid #e5c358aa',boxShadow:'0 0 90px #e5c35819,inset 0 0 85px #e5c35813',
      transform:'scale('+(.9+.1*p)+')',opacity:p,
    }}/>
    <CenterText y={795} size={47} alpha={p}>الفائض المتوقع</CenterText>
    <CenterText y={914} size={184} alpha={p} gold>1,700</CenterText>
    <CenterText y={1138} size={47} alpha={p}>ريال سعودي</CenterText>
    <FocusBar y={1320} alpha={p}/>
  </AbsoluteFill>;
};

const ExpenseShot=()=>{
  const f=useCurrentFrame();const input=.75+.25*reveal(f,0,12),saved=reveal(f,70,17);
  const value=Math.round(interpolate(f,[74,96],[1700,1675],{extrapolateLeft:'clamp',extrapolateRight:'clamp'}));
  return <AbsoluteFill>
    <Gradient/><Logo/>
    <CenterText y={495} size={78} alpha={input*(1-reveal(f,81,14))}>قهوة بـ25 ريال؟</CenterText>
    <CenterText y={495} size={79} alpha={saved}>تسجّلت.</CenterText>
    <div style={{
      position:'absolute',left:165,right:165,top:790,height:380,
      border:'2px solid #e5c358aa',borderRadius:48,background:'#102318',boxShadow:'0 25px 88px #000a',
      display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',gap:12,
      transform:'scale('+(.96+.04*input)+')',
    }}>
      <span style={{fontSize:37,color:T.muted}}>{saved>.5?'الفائض بعد القهوة':'مصروف جديد'}</span>
      <span style={{fontSize:147,fontWeight:800,color:T.gold,fontFamily:'Arial'}}>{saved>.5?value:'25'}</span>
      <span style={{fontSize:37,color:T.muted}}>ر.س</span>
    </div>
    <FocusBar y={1282} alpha={input}/>
  </AbsoluteFill>;
};

const SaveShot=()=>{
  const f=useCurrentFrame(),p=.72+.28*reveal(f,0,12);
  return <AbsoluteFill>
    <Gradient/><Logo/>
    <div style={{position:'absolute',left:94,right:94,top:590,borderRadius:35,
      padding:13,background:'#111d15',border:'1px solid #e5c3585a',
      boxShadow:'0 34px 100px #0009',opacity:p,
      transform:'scale('+(.94+.06*p)+')'}}>
      <Img src={staticFile('screens/dashboard.png')} style={{display:'block',width:'100%',borderRadius:23}}/>
    </div>
    <div style={{position:'absolute',top:1320,left:110,right:110,padding:'29px 12px',
      textAlign:'center',background:'#122c1e',borderRadius:35,
      border:'1px solid #e5c35888',color:'#fff7db',fontWeight:800,fontSize:63,opacity:p,
    }}>✓ محفوظة على جهازك</div>
    <FocusBar y={1520} alpha={p}/>
  </AbsoluteFill>;
};

const ClosingShot=()=>{
  const f=useCurrentFrame(),p=.7+.3*reveal(f,0,14);
  return <AbsoluteFill>
    <Gradient/>
    <div style={{position:'absolute',left:0,right:0,top:450,display:'flex',justifyContent:'center',
      opacity:p,transform:'scale('+(.88+.12*p)+')'}}>
      <Img src={staticFile('brand/promax.png')} style={{width:280,height:280,borderRadius:63,
        boxShadow:'0 0 110px #e5c35833'}}/>
    </div>
    <CenterText y={790} size={138} alpha={p}>ProMax<span style={{color:T.gold}}>.</span></CenterText>
    <CenterText y={1010} size={55} alpha={reveal(f,25,12)}>رتّب راتبك بوضوح.</CenterText>
    <CenterText y={1145} size={86} gold alpha={reveal(f,34,12)}>$29.99</CenterText>
    <div dir="rtl" style={{position:'absolute',top:1315,left:150,right:150,height:120,
      borderRadius:28,display:'grid',placeItems:'center',fontSize:57,fontWeight:800,
      background:T.gold,color:'#09170e',boxShadow:'0 0 70px #e5c3584a',
      opacity:reveal(f,42,13),transform:'translateY('+(25*(1-reveal(f,42,13)))+'px)',
    }}>{CONTENT.cta}</div>
  </AbsoluteFill>;
};

const shots=[FaceShot,PhoneShot,SofaShot,DashboardShot,SurplusShot,ExpenseShot,SaveShot,ClosingShot];

export const DirectorCut=()=>{
  const global=useCurrentFrame();
  return <AbsoluteFill style={{background:'#070e0a',overflow:'hidden',
    fontFamily:'Tajawal, Arial, sans-serif',color:'#fff9e8'}}>
    {SCENES.map((scene,index)=>{
      const Shot=shots[index];
      const body=<CameraShot scene={scene}><Shot/></CameraShot>;
      if(scene.start===0)return <Sequence key={scene.name} name={scene.name} durationInFrames={scene.end}>{body}</Sequence>;
      return <Sequence key={scene.name} name={scene.name} from={scene.start}
        durationInFrames={scene.end-scene.start} premountFor={10}>{body}</Sequence>;
    })}
    <SalaryReceipt frame={global}/>
    <div style={{position:'absolute',left:120,right:120,top:1730,height:2,background:'#ffffff15'}}>
      <div style={{width:(100*global/749)+'%',height:'100%',background:T.gold,opacity:.6}}/>
    </div>
  </AbsoluteFill>;
};
